import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { apiUser } from "../../Apis/configs/axiosConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid, GridExpandMoreIcon } from "@mui/x-data-grid";
import { SpinnerCircular } from "spinners-react";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  IconButton,
  Stack,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import { blue, blueGrey, green, grey, yellow } from "@mui/material/colors";
import { ArrowBack } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import LinearProgress from "@mui/material/LinearProgress";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";


const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "white",
  borderRadius: "8px",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  textAlign: "center",
};

const labelStyle = {
  fontSize: "18px",
  fontWeight: "normal",
  marginBottom: "10px",
  color: "#032C65",
};


const MobileTable = React.memo(function MobileTable({ data, t, editZone, deactivateZone }) {
  return (
    <Grid item container xs={12} justifyContent="center">
      {data.map((row) => (
        <Grid item xs={10} sm={8} key={`zone-mobile-table-${row.zone_number}`}>
          <Accordion
            style={{
              backgroundColor: "white",
              color: "black",
              border: "1px solid grey",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(5, 11, 0, 0.3)",
              marginBottom: "16px",
            }}
            key={`zone-list-item-${row.zone_number}`}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}> 
              <Grid item container xs={12}>
                <Grid item container xs={4}>
                  <Typography
                    style={{
                      display: "flex",
                      color: "#032C65",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      fontWeight: "bold",
                    }}
                  >
                    {row.zone_name ? row.zone_name : row.zone_number}
                  </Typography>
                </Grid>
                <Grid item container xs={8}>
                  <Typography
                    style={{
                      display: "flex",
                      color: "#032C65",
                      flexDirection: "column",
                      fontFamily: "monospace",
                      alignItems: "flex-start",
                      fontWeight: "normal",
                    }}
                  >
                    {row.decoder}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails
              style={{
                color: "#032C65",
                fontWeight: "normal",
                marginRight: "8px",
                textAlign: "left",
                marginBottom: "8px",
              }}
            >
              <Typography style={{ color: "#032C65", fontWeight: "normal" }}>{t(`Zone Name`)}:</Typography>
              <Typography style={{ color: "black", marginRight: "8px", textAlign: "left", marginBottom: "8px" }}>
                {`${row.zone_name || "N/A"}`}
              </Typography>

              <Typography style={{ color: "#032C65", fontWeight: "normal" }}>{t(`Decoder S/N`)}</Typography>
              <Typography style={{ color: "black", fontSize: "13px", marginRight: "8px", textAlign: "left", marginBottom: "8px" }}>
                {`${row.decoder || "N/A"}`}
              </Typography>

              <Typography style={{ color: "#032C65", fontWeight: "normal" }}>{t(`Decoder Type`)}:</Typography>
              {row.decoder !== "000000" ? (
                <Chip
                  label={
                    row.zone_type === 1 ? t("Zone") : row.zone_type === 2 ? "MV/PS" : row.zone_type === 3 ? t("Flow meter") : t("Moisture sensor")
                  }
                  size="small"
                  sx={{ backgroundColor: row.zone_type === 1 ? green[100] : grey[200], marginBottom: 2 }}
                />
              ) : (
                <Chip label="N/A" size="small" sx={{ backgroundColor: grey[200], color: grey[600] }} />
              )}

              <Typography style={{ color: "#032C65", fontWeight: "normal" }}>{t(`Wire`)}:</Typography>
              <Typography style={{ color: "black", fontSize: "13px", marginRight: "8px", textAlign: "left", marginBottom: "8px" }}>
                {`${row.zone_wire || "N/A"}`}
              </Typography>

              <AccordionDetails
                style={{
                  textAlign: "justify",
                  color: "#032C65",
                  padding: "16px",
                  borderTop: "1px solid #ccc",
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  size="large"
                  style={{
                    backgroundColor: "#032C65",
                    border: "1px solid grey",
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(5, 11, 0, 0.4)",
                    fontSize: "15px",
                    fontWeight: "normal",
                    color: "white",
                  }}
                  startIcon={<EditIcon />}
                  onClick={() => editZone(row)}
                >
                  {t(`Edit`)}
                </Button>
                <Button
                  size="large"
                  style={{
                    backgroundColor: "#d33",
                    border: "1px solid grey",
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(5, 11, 0, 0.4)",
                    fontSize: "15px",
                    fontWeight: "normal",
                    color: "white",
                  }}
                  startIcon={<RemoveCircleOutlineIcon />}
                  onClick={() => deactivateZone(row)}
                >
                  {t(`Deactivate`)}
                </Button>
              </AccordionDetails>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}
    </Grid>
  );
});

const TableZoneList = React.memo(function TableZoneList({
  zones,
  columns,
  rowSelectionModel,
  onSelectionChange,
  isSmallScreen,
  t,
  editZone,
  deactivateZone,
}) {
  const mapData = useMemo(
    () =>
      zones.map(({ decoder, zone_name, zone_wire, zone_type, zone_number }) => ({
        zone_number,
        decoder,
        zone_name,
        zone_wire,
        zone_type,
      })),
    [zones]
  );

  if (isSmallScreen) {
    return (
      <MobileTable
        data={mapData}
        t={t}
        editZone={editZone}
        deactivateZone={deactivateZone}
      />
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={10} sm={10} md={10} lg={10}>
        <DataGrid
          style={{
            backgroundColor: "white",
            border: "2",
            borderRadius: "15px",
            boxShadow: "0 4px 8px rgba(5, 11, 0, 0.1)",
            fontSize: "15px",
            height: 700,
          }}
          getRowId={(row) => row.zone_number}
          hideFooter
          rows={zones}
          columns={columns}
          rowSelectionModel={rowSelectionModel}
          disableRowSelectionOnClick={false}
          keepNonExistentRowsSelected={true}
          onRowSelectionModelChange={onSelectionChange}
        />
      </Grid>
    </Grid>
  );
});


export const ZonesTable = () => {
  const [zones, setZones] = useState([]);
  const [editedZones, setEditedZones] = useState([]);
  const [removeState, setRemoveState] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const [selectedRow, setSelectedRow] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    decoder: "000000",
    wire: 1,
    zone_type: 1,
    zone_number: undefined,
  });
  const [loadingBox, setloadingBox] = useState(false);
  const [loadingProgressBar, setLoadingProgressBar] = useState(false);
  let [ProgressBarSteps, setProgressBarSteps] = useState(null);
  const [progressBarColor, setProgressBarColor] = useState("primary");
  const navigate = useNavigate();
  const [changedZones, setChangedZones] = useState(false);


  const prevZonesRef = useRef([]);
  const selectedZoneNumberRef = useRef(null);
  const prevZonesDataRef = useRef();
  const isInitialLoadRef = useRef(true);


  const openBox = useCallback(() => setOpen(true), []);
  const closebox = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      if (selectedZoneNumberRef.current) {
        const currentZone = zones.find((z) => z.zone_number === selectedZoneNumberRef.current);
        if (currentZone) {
          setSelectedRow(currentZone);
          setRowSelectionModel([selectedZoneNumberRef.current]);
        }
      }
    }, 100);
  }, [zones]);

  const restoreSelection = useCallback(() => {
    if (selectedZoneNumberRef.current && zones.length > 0) {
      const currentZone = zones.find((z) => z.zone_number === selectedZoneNumberRef.current);
      if (currentZone) {
        setSelectedRow(currentZone);
        setRowSelectionModel([selectedZoneNumberRef.current]);
        return true;
      }
    }
    return false;
  }, [zones]);


  const getZones = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && !isInitialLoadRef.current) return;
      setIsLoading(true);
      try {
        const zonesResponse = await apiUser.get(`/controllers/${location.state.id}`);
        const zonesData = zonesResponse.data.data.zones || [];

        const zonesNormalized = zonesData.map((zone, index) => {
          const zone_number = zone.zone_number ?? index + 1;
          return {
            ...zone,
            zone_number,
            zone_name: zone.zone_name || `${t("Zone")} ${zone_number}`,
          };
        });


        const zonesChanged = JSON.stringify(zonesNormalized) !== JSON.stringify(prevZonesRef.current);
        if (zonesChanged || forceRefresh) {
          prevZonesRef.current = zonesNormalized;
          setZones(zonesNormalized);

          if (selectedZoneNumberRef.current) {
            const stillExists = zonesNormalized.find((z) => z.zone_number === selectedZoneNumberRef.current);
            if (stillExists) {
              setRowSelectionModel([selectedZoneNumberRef.current]);
              const currentRow = zonesNormalized.find((z) => z.zone_number === selectedZoneNumberRef.current);
              if (currentRow) setSelectedRow(currentRow);
            }
          }

          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          title: "Error",
          text: t("Error occured, back to my controllers."),
          icon: "warning",
          timer: 2000,
        }).then(() => {
          navigate("/mycontrollers");
        });
      }
      setIsLoading(false);
    },
    [location.state.id, t, navigate]
  );

  useEffect(() => {
    getZones(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (zones.length > 0 && selectedZoneNumberRef.current) {
      restoreSelection();
    }
  }, [zones, restoreSelection]);

  // Polling realtime
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await apiUser.get(`/controllers/${location.state.id}/realtime`);
        const newZonesData = resp.data.data.loading_state_change;
        if (newZonesData !== null) {
          setloadingBox(true);
          setProgressBarSteps((prevSteps) => Math.min((prevSteps ?? 0) + 1, 3));
          prevZonesDataRef.current = newZonesData;
        } else if (newZonesData === null && ProgressBarSteps === 0) {
          // no-op
        } else if (newZonesData == null && ProgressBarSteps > 0) {
          setProgressBarColor("success");
          setTimeout(() => {
            setLoadingProgressBar(false);
            setloadingBox(false);
            setProgressBarSteps(null);
            setLoadingProgressBar(false);
          }, 2000);
        }
      } catch (e) {
        console.error(e);
        setLoadingProgressBar(false);
      }
    };

    const timer = setInterval(fetchData, 6000);
    return () => clearInterval(timer);
  }, [ProgressBarSteps, location.state.id]);

  // Editar zona
  const editZone = useCallback(
    (item) => {
      selectedZoneNumberRef.current = item.zone_number;
      openBox();
      setSelectedRow(item);
      setRowSelectionModel([item.zone_number]);
      setFormData({
        zone_number: item.zone_number,
        name: item.zone_name || "",
        decoder: item.decoder || "",
        wire: item.zone_wire || "",
        zone_type: item.zone_type || 1,
        assigned_pumps: [true, true, false, true, false],
      });
    },
    [openBox]
  );

  // Confirmar edición
  const confirmEditZoneTime = useCallback(() => {
    const newRunTimeInfo = { ...formData };

    if (newRunTimeInfo.name && newRunTimeInfo.name.length > 12) {
      closebox();
      Swal.fire({
        title: t("Edit Zone"),
        text: t("Zone name cannot exceed 12 characters"),
        icon: "warning",
        confirmButtonColor: "#032C65",
        confirmButtonText: t("Confirm"),
      });
      return;
    }

    if (newRunTimeInfo.decoder === "000000") {
      closebox();
      Swal.fire({
        title: t("Edit Zone"),
        text: t("You need to set a valid decoder"),
        icon: "info",
        confirmButtonColor: "#032C65",
        confirmButtonText: t("Confirm"),
      });
      return;
    }

    const isDecoderInUse = zones.some(
      (zone) => zone.decoder === newRunTimeInfo.decoder && zone.zone_number !== newRunTimeInfo.zone_number
    );
    if (isDecoderInUse) {
      closebox();
      Swal.fire({
        title: t("Edit Zone"),
        text: t("This decoder is already in use in another zone"),
        icon: "warning",
        confirmButtonColor: "#032C65",
        confirmButtonText: t("Confirm"),
      });
      return;
    } else {
      const newArr = [
        newRunTimeInfo,
        ...editedZones.filter((item) => item.zone_number !== newRunTimeInfo.zone_number),
      ];
      setEditedZones(newArr);

      const index = zones.findIndex((zone) => zone.zone_number === formData.zone_number);
      if (index !== -1) {
        const updatedZone = {
          ...zones[index],
          zone_name: formData.name,
          decoder: formData.decoder,
          zone_wire: formData.wire,
          zone_type: formData.zone_type,
        };
        const updatedZones = [...zones];
        updatedZones[index] = updatedZone;
        setZones(updatedZones);
        setSelectedRow(updatedZone);
        setRowSelectionModel([updatedZone.zone_number]);
        selectedZoneNumberRef.current = updatedZone.zone_number;
        setChangedZones(true);
      }
      closebox();
    }
  }, [formData, zones, editedZones, t, closebox]);

  // Desactivar zona
  const deactivateZone = useCallback(
    (zone) => {
      selectedZoneNumberRef.current = zone.zone_number;
      setSelectedRow(zone);
      setRowSelectionModel([zone.zone_number]);

      if (zone) {
        Swal.fire({
          title: t("Deactivate Zone"),
          text: t(`Are you sure to deactivate `) + `${zone.zone_name}`,
          icon: "question",
          confirmButtonColor: "#d33",
          confirmButtonText: t("Deactivate"),
          showCancelButton: true,
          cancelButtonColor: "#032C65",
          cancelButtonText: t("Cancel"),
        }).then((data) => {
          if (data.isConfirmed) {
            setRemoveState(true);
            let obj = {
              zone_number: zone.zone_number,
              name: t("Zone") + ` ${zone.zone_number}`,
              decoder: "000000",
              wire: 1,
              zone_type: 1,
              assigned_pumps: [true, true, false, true, false],
            };

            if (zone.decoder === "000000") {
              return Swal.fire({
                title: t("Zone already deactivated"),
                text: t("This zone has already been deactivated."),
                icon: "info",
                confirmButtonColor: "#032C65",
                confirmButtonText: t("Confirm"),
              });
            } else {
              Swal.fire({
                title: t("Zone Deactivated"),
                text: t("This zone has been deactivated and reset to default settings."),
                icon: "success",
                confirmButtonColor: "#032C65",
                confirmButtonText: t("Confirm"),
              }).then((data) => {
                if (data.isConfirmed) {
                  setChangedZones(true);
                  const newRunTimeInfo = { ...obj };
                  const newArr = [
                    newRunTimeInfo,
                    ...editedZones.filter((item) => item.zone_number !== newRunTimeInfo.zone_number),
                  ];
                  setEditedZones(newArr);

                  const updatedZones = zones.map((z) =>
                    z.zone_number === zone.zone_number
                      ? {
                          ...z,
                          decoder: "000000",
                          zone_name: t("Zone") + ` ${zone.zone_number}`,
                          zone_wire: 1,
                          zone_type: 1,
                        }
                      : z
                  );

                  setZones(updatedZones);
                  const current = updatedZones.find((z) => z.zone_number === zone.zone_number);
                  setSelectedRow(current);
                  setRowSelectionModel([zone.zone_number]);
                  selectedZoneNumberRef.current = zone.zone_number;
                }
              });
            }
          }
        });
      }
    },
    [zones, editedZones, t]
  );

  // Guardar cambios
  const saveChanges = useCallback(
    async () => {
      setloadingBox(true);
      let body_request = {
        zones: editedZones,
      };

      try {
        const request = await apiUser.put(
          `/controllers/${location.state.id}/update_data/zones`,
          body_request
        );
        if (request.data.success) {
          setProgressBarColor("primary");
          setProgressBarSteps(0);
          setChangedZones(false);
          setEditedZones([]);
          setLoadingProgressBar(true);

          // Preservar selección tras guardar
          if (selectedZoneNumberRef.current) {
            setRowSelectionModel([selectedZoneNumberRef.current]);
            const currentZone = zones.find((z) => z.zone_number === selectedZoneNumberRef.current);
            if (currentZone) setSelectedRow(currentZone);
          }
        }
      } catch (e) {
        console.error(e);
        Swal.fire({
          title: "Error",
          text: t("Error, has been occurred."),
          icon: "warning",
          timer: 2000,
        });
        setloadingBox(false);
      }
      setIsLoading(false);
      closebox();
    },
    [editedZones, location.state.id, t, closebox, zones]
  );

  // Columnas
  const columns = useMemo(
    () => [
      {
        field: "zone_type",
        headerName: t("Decoder Type"),
        flex: 2,
        align: "left",
        headerClassName: "header-table-title-style",
        renderCell: ({ row }) => {
          let label = "";
          switch (row.zone_type) {
            case 1:
              label = t("Zone");
              break;
            case 2:
              label = t("MV/PS");
              break;
            case 3:
              label = t("Flow meter");
              break;
            case 4:
              label = t("Moisture sensor");
              break;
            default:
          }
          return row.decoder !== "000000" ? (
            <Chip label={label} size="small" sx={{ backgroundColor: green[100] }} />
          ) : (
            <Chip label="N/A" size="small" sx={{ backgroundColor: grey[200], color: grey[600] }} />
          );
        },
      },
      { field: "zone_name", headerName: t("Name"), flex: 2, headerClassName: "header-table-title-style" },
      { field: "decoder", headerName: t("Decoder S/N"), flex: 2, headerClassName: "header-table-title-style" },
      { field: "zone_wire", headerName: t("Wire"), flex: 2, headerClassName: "header-table-title-style" },
      {
        field: "edit",
        headerName: t("Actions"),
        headerClassName: "header-table-title-style",
        renderCell: (params) => (
          <>
            <Button startIcon={<EditIcon />} style={{ alignContent: "flex-start", color: "#032C65" }} onClick={() => editZone(params.row)} />
            <Button
              style={{ border: "none", width: "50%" }}
              onClick={() => deactivateZone(params.row)}
              variant="outlined"
              startIcon={<RemoveCircleOutlineIcon style={{ marginRight: 34, color: "#800000" }} />}
            />
          </>
        ),
      },
    ],
    [t, editZone, deactivateZone]
  );

  const isSmallScreen = useMediaQuery("(max-width:800px)");

  return (
    <>
      <Grid container item xs={12} justifyContent="center">
        <Grid
          sx={{ borderRadius: "8px", border: `1px solid ${grey[300]}`, backgroundColor: "#F7F8FA", marginBottom: 4, padding: 2 }}
          container
          xs={10}
          justifyContent="start"
        >
          <Grid sx={{ borderRadius: "8px" }} item container xs={12} sm={2} component={Stack} alignItems="center" justifyContent="center">
            <IconButton
              size="large"
              sx={{ backgroundColor: "white" }}
              onClick={() => navigate(`/dashboard/${location.state.id}`, { state: location.state })}
            >
              <ArrowBack />
            </IconButton>
          </Grid>

          <Grid item container elevation={0} xs={11} sm={10} justifyContent="space-between" alignItems={"center"}>
            <Typography variant="h5" fontWeight="bolder" color="#6B7A99">
              {t(`Zones`)}
            </Typography>

            <Grid item>
              <LoadingButton
                loading={loadingBox}
                className="button-new-controller-style"
                variant="contained"
                size="large"
                onClick={saveChanges}
                disabled={!changedZones}
                sx={{
                  backgroundColor: "white",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                  boxShadow: changedZones ? 4 : 0,
                  color: blueGrey[600],
                  borderRadius: 4,
                  "&:hover": { backgroundColor: "white", boxShadow: 6, color: blue[600] },
                }}
                startIcon={!loadingBox && <SaveIcon style={{ fontSize: 25, color: changedZones === true ? yellow[600] : "white" }} />}
              >
                {t("Save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>

        {loadingProgressBar && (
          <>
            <Grid item container xs={12} justifyContent="center">
              <Typography id="modal-modal-title" variant="h6" component="h2">
                {ProgressBarSteps == 0 && t("Initializing the update process...")}
                {ProgressBarSteps == 1 && t("Synchronizing with your device....")}
                {ProgressBarSteps == 2 && t("Applying changes to your device...")}
                {ProgressBarSteps == 3 && progressBarColor == "primary" && t("Finalizing changes to your controller...")}
                {ProgressBarSteps == 3 && progressBarColor == "success" && t("Zones are already updated.")}
              </Typography>
            </Grid>
            <LinearProgress sx={{ width: "83%", marginBottom: 2, borderRadius: 12 }} color={progressBarColor} variant="determinate" value={((ProgressBarSteps ?? 0) / 3) * 100} />
          </>
        )}

        <Grid item container xs={12} justifyContent="center">
          {isLoading ? (
            <SpinnerCircular />
          ) : (
            <TableZoneList
              zones={zones}
              columns={columns}
              rowSelectionModel={rowSelectionModel}
              isSmallScreen={isSmallScreen}
              t={t}
              editZone={editZone}
              deactivateZone={deactivateZone}
              onSelectionChange={(newModel) => {
                setRowSelectionModel(newModel);
                const id = newModel[0];
                if (id !== undefined) {
                  const row = zones.find((z) => z.zone_number === id);
                  if (row) {
                    setSelectedRow(row);
                    selectedZoneNumberRef.current = id;
                  }
                } else {
                  setSelectedRow(null);
                  selectedZoneNumberRef.current = null;
                }
              }}
            />
          )}
        </Grid>
      </Grid>

      <Modal open={open} onClose={closebox} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description" disableScrollLock>
        <Box sx={modalStyle}>
          <Grid container justifyContent="end">
            <CloseIcon size="large" color="primary" sx={{ right: 5, top: "2%", cursor: "pointer" }} onClick={closebox} />
          </Grid>

          <div style={labelStyle}>
            {t(`Edit Zone`)}
            <br />
            <br />
          </div>

          <label style={{ display: "block", color: "#032C65", fontWeight: "normal", marginBottom: "10px" }}>{t(`Name`)}:</label>
          <input
            type="text"
            placeholder={formData.name || ""}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            maxLength={12}
            style={{ width: "95%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", textAlign: "center" }}
          />

          <label style={{ display: "block", marginBottom: "10px", color: "#032C65", fontWeight: "normal", marginTop: "10px" }}>{t(`Decoder S/N`)}</label>
          <input
            placeholder={formData.decoder || ""}
            maxLength={6}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 6) setFormData({ ...formData, decoder: value });
            }}
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && !["Backspace", "Tab"].includes(e.key)) e.preventDefault();
            }}
            style={{ width: "95%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", textAlign: "center" }}
          />

          <label style={{ display: "block", marginBottom: "10px", color: "#032C65", fontWeight: "normal", marginTop: "10px" }}>{t(`Decoder type`)}:</label>
          <Select
            value={formData.zone_type || 1}
            onChange={(e) => setFormData({ ...formData, zone_type: Number(e.target.value) })}
            style={{ width: "100%", padding: "1px", borderRadius: "4px", border: "1px solid #ccc", textAlign: "center", height: "38px" }}
          >
            <MenuItem value={1}>{t(`Zone`)}</MenuItem>
            <MenuItem value={2}>MV/PS</MenuItem>
            {/* <MenuItem value={3}>{t(`Flow meter`)}</MenuItem>
            <MenuItem value={4}>{t(`Moisture sensor`)}</MenuItem> */}
          </Select>

          <label style={{ display: "block", marginBottom: "10px", color: "#032C65", fontWeight: "normal", marginTop: "10px" }}>{t(`Wire`)}:</label>
          <Select
            value={formData.wire || 1}
            onChange={(e) => setFormData({ ...formData, wire: Number(e.target.value) })}
            style={{ width: "100%", padding: "1px", borderRadius: "4px", border: "1px solid #ccc", textAlign: "center", height: "38px" }}
          >
            <MenuItem value={1}>{t(`TW 1`)}</MenuItem>
            <MenuItem value={2}>{t(`TW 2`)}</MenuItem>
          </Select>

          <LoadingButton
            variant="contained"
            loadingPosition="start"
            onClick={confirmEditZoneTime}
            style={{
              marginTop: "20px",
              width: "50%",
              backgroundColor: "#032C65",
              color: "white",
              fontWeight: "normal",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(5, 11, 0, 0.4)",
            }}
          >
            {t(`Confirm`)}
          </LoadingButton>
        </Box>
      </Modal>
    </>
  );
};
